import { Person } from "@/types";

export enum ActivityParticipationRole {
  HOST_PROFESSIONAL = "HOST_PROFESSIONAL",
  EXAMINER = "EXAMINER",
  ATTENDEE = "ATTENDEE",
  ONE_TO_FOUR_VOLUNTEER_HOURS = "ONE_TO_FOUR_VOLUNTEER_HOURS",
  FIVE_TO_EIGHT_VOLUNTEER_HOURS = "FIVE_TO_EIGHT_VOLUNTEER_HOURS"
}

export enum AttendanceType {
  IN_PERSON = "IN_PERSON",
  VIRTUAL = "VIRTUAL"
}

export enum RateUnit {
  HOUR = "HOUR",
  DAY = "DAY",
  WEEK = "WEEK",
  EVENT_SESSION = "EVENT_SESSION",
  EVENT = "EVENT",
  SEASON = "SEASON",
  INSTANCE = "INSTANCE"
}

export enum EventAttendanceRequirement {
  ALL_REGISTERED_SESSIONS = "ALL_REGISTERED_SESSIONS",
  ANY_SESSION = "ANY_SESSION"
}

export enum SectionAvailability {
  CAN_CREATE = "CAN_CREATE",
  CAN_VIEW = "CAN_VIEW"
}

export enum CreatesEventType {
  OPTIONAL = "OPTIONAL",
  ALWAYS = "ALWAYS",
  NEVER = "NEVER"
}

/** Activity type title kinds used with isActivityType() in _utils */
export type ActivityTypeKind =
  | "education-self-paced"
  | "instructor-led"
  | "education-self-reported"
  | "education"
  | "pat"
  | "national-meeting"
  | "championship"
  | "recruitment";

export interface ActivityType {
  id: string;
  title?: string;
  createsEvent: CreatesEventType;
  activityType: string;
  activityCode: string;
  slug: string;
  description: string;
  creditRate: string;
  verification: string;
  publishedActivities: number | null;
  config?: ActivityConfig;
  sectionAvailability: SectionAvailability;
  allowsParticipation?: boolean;
  activities?: {
    totalCount: number;
  };
}

export interface ActivityTypeTableRow {
  id: string;
  title?: string;
  activityType: string | undefined;
  activityCode: string;
  slug: string;
  description: string;
  creditRate: string | null;
  verification: string;
  publishedActivities: number;
  sectionAvailability: SectionAvailability;
  config?: ActivityConfig;
  activities?: {
    totalCount: number;
  };
}

export interface CreditRate {
  id: string;
  rateMultiplier: number;
  rateUnit: RateUnit;
  participationRole: ActivityParticipationRole;
  attendanceType: AttendanceType;
  legacyCreditType: string;
}

export type ParticipationActivityTypeRef = {
  id: string;
  title: string;
  description?: string;
  sectionAvailability: SectionAvailability;
  createsEvent: CreatesEventType;
  config?: {
    creditRates: CreditRate[];
  };
};

export type ParticipationLinkedActivityRef = {
  id: string;
  title?: string;
  allowParticipationStarting?: string | null;
  allowParticipationThrough?: string | null;
  duration?: number | null;
  activityType: ParticipationActivityTypeRef;
  event?: {
    slug?: string;
    title?: string;
    startsAt?: string;
    endsAt?: string;
  };
  managingSection?: {
    id: string;
  } | null;
};

export type LinkedActivityParticipation = {
  id: string;
  title: string;
  canEdit: boolean;
  canDelete: boolean;
  isTypeLevelParticipation: boolean;
  role: ActivityParticipationRole;
  attendanceType?: AttendanceType;
  quantity?: number | null;
  activityType: ParticipationActivityTypeRef;
  activity?: ParticipationLinkedActivityRef | null;
};

export interface ActivityConfig {
  id: string;
  sectionAvailability: SectionAvailability;
  creditRates: CreditRate[];
}

export interface ParticipantsNode {
  id: string;
  title: string;
  canEdit?: boolean;
  canDelete?: boolean;
  role: ActivityParticipationRole;
  createdAt: string;
  beganAt: string;
  endedAt: string;
  attendanceType: AttendanceType;
  isTypeLevelParticipation: boolean;
  quantity?: number | null;
  managingSection?: {
    id: string;
    name: string;
  } | null;
  credit: {
    id: string;
    creditsAccepted?: number;
  };
  person: Person;
  activity?: {
    id: string;
    title: string;
    activityType: {
      title: string;
      description: string;
    };
    event: {
      slug: string;
      title: string;
      startsAt?: string;
      endsAt?: string;
      status?: string;
      facility?: { name: string };
    };
  } | null;
}

export type ActivityParticipation = {
  id: string;
  activity: ParticipationLinkedActivityRef | null;
  isTypeLevelParticipation: boolean;
  managingSection?: {
    id: string;
    name: string;
  } | null;
  canEdit: boolean;
  canDelete: boolean;
  eventSlug: string;
  title: string;
  pgaId?: string;
  activityType: {
    id: string;
    title: string;
    description: string;
    sectionAvailability: SectionAvailability;
    createsEvent: CreatesEventType;
    config: {
      creditRates: CreditRate[];
      attendanceType: AttendanceType;
    };
  };
  activityDescription: string;
  series?: string;
  hoursServed?: number;
  creditsAwarded?: number;
  creditsRequested?: number;
  startDate?: string;
  endDate?: string;
  location?: string;
  submittedAt?: string;
  author?: string;
  creditsAppliedFor?: number;
  description: string;
  attendanceType: AttendanceType;
  role: ActivityParticipationRole;
  quantity: number | null;
  createdAt: string;
  beganAt: string;
  endedAt: string;
  credit: {
    id: string;
    activityCode?: string;
    description1?: string;
    description2?: string;
    creditType?: string;
  };
  person: Person;
};
